import React from "react";
import {
  makeStyles,
  tokens,
  Label,
  ToggleButton,
  useId,
} from "@fluentui/react-components";
import { ExceptionFilter, LiquidationFilter, TimePeriodType } from "../types";
import { CountryDropdown } from "./CountryDropdown";
import { SalesPersonDropdown } from "./SalesPersonDropdown";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
  },
  filterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
  buttonGroup: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalXS,
  },
});

interface DataFiltersProps {
  liquidationFilter: LiquidationFilter;
  onLiquidationFilterChange: (value: LiquidationFilter) => void;
  exceptionFilter: ExceptionFilter;
  onExceptionFilterChange: (value: ExceptionFilter) => void;
  timePeriod: TimePeriodType;
  onTimePeriodChange: (value: TimePeriodType) => void;
  countries: string[];
  countriesLoading: boolean;
  selectedCountries: string[];
  onSelectedCountriesChange: (countries: string[]) => void;
  salesPeople: string[];
  salesPeopleLoading: boolean;
  selectedSalesPeople: string[];
  onSelectedSalesPeopleChange: (salesPeople: string[]) => void;
}

const liquidationOptions: { value: LiquidationFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "none", label: "None" },
  { value: "only", label: "Only" },
];

const exceptionOptions: { value: ExceptionFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "none", label: "None" },
  { value: "only", label: "Only" },
];

const timePeriodOptions: { value: TimePeriodType; label: string }[] = [
  { value: "ytd", label: "YTD" },
  { value: "periodic", label: "Periodic" },
];

export const DataFilters: React.FC<DataFiltersProps> = ({
  liquidationFilter,
  onLiquidationFilterChange,
  exceptionFilter,
  onExceptionFilterChange,
  timePeriod,
  onTimePeriodChange,
  countries,
  countriesLoading,
  selectedCountries,
  onSelectedCountriesChange,
  salesPeople,
  salesPeopleLoading,
  selectedSalesPeople,
  onSelectedSalesPeopleChange,
}) => {
  const styles = useStyles();
  const exceptionsLabelId = useId("exceptions-label");

  return (
    <div className={styles.container}>
      {/* Liquidation Filter */}
      <div className={styles.filterGroup}>
        <Label weight="semibold">Liquidation</Label>
        <div className={styles.buttonGroup}>
          {liquidationOptions.map((option) => (
            <ToggleButton
              key={option.value}
              checked={liquidationFilter === option.value}
              onClick={() => onLiquidationFilterChange(option.value)}
              appearance={
                liquidationFilter === option.value ? "primary" : "outline"
              }
              size="small"
            >
              {option.label}
            </ToggleButton>
          ))}
        </div>
      </div>

      <div className={styles.filterGroup}>
        <Label id={exceptionsLabelId} weight="semibold">
          Exceptions
        </Label>
        <div
          className={styles.buttonGroup}
          role="group"
          aria-labelledby={exceptionsLabelId}
        >
          {exceptionOptions.map((option) => (
            <ToggleButton
              key={option.value}
              checked={exceptionFilter === option.value}
              onClick={() => onExceptionFilterChange(option.value)}
              appearance={
                exceptionFilter === option.value ? "primary" : "outline"
              }
              size="small"
            >
              {option.label}
            </ToggleButton>
          ))}
        </div>
      </div>

      {/* Time Period */}
      <div className={styles.filterGroup}>
        <Label weight="semibold">Period</Label>
        <div className={styles.buttonGroup}>
          {timePeriodOptions.map((option) => (
            <ToggleButton
              key={option.value}
              checked={timePeriod === option.value}
              onClick={() => onTimePeriodChange(option.value)}
              appearance={timePeriod === option.value ? "primary" : "outline"}
              size="small"
            >
              {option.label}
            </ToggleButton>
          ))}
        </div>
      </div>

      {/* Country Filter */}
      <div className={styles.filterGroup}>
        <CountryDropdown
          countries={countries}
          isLoading={countriesLoading}
          selectedCountries={selectedCountries}
          onSelectionChange={onSelectedCountriesChange}
        />
      </div>

      {/* SalesPerson Filter */}
      <div className={styles.filterGroup}>
        <SalesPersonDropdown
          salesPeople={salesPeople}
          isLoading={salesPeopleLoading}
          selectedSalesPeople={selectedSalesPeople}
          onSelectionChange={onSelectedSalesPeopleChange}
        />
      </div>
    </div>
  );
};
